/**
 * ProgrammeTab — File d'affichage éditoriale du board
 *
 * Affiche en temps réel ce qui est en cours de diffusion sur la tablette :
 *   - Couche 1 : file du jour (display_queue), réordonnables par drag & drop
 *   - Couche 2 : statistiques du pool d'archives (lecture seule côté admin)
 *
 * Photos : vignette cliquable → lightbox Radix Dialog (même pattern que l'historique).
 * Drag & drop : API HTML5 native (admin desktop uniquement).
 * Realtime : invalidation TanStack Query à chaque event sur display_queue.
 */

import { useEffect, useRef, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Card,
  Dialog,
  Flex,
  IconButton,
  Skeleton,
  Text,
  Tooltip,
} from "@radix-ui/themes";
import {
  GripVertical,
  Image,
  MessageSquare,
  CloudSun,
  Trash2,
  ListOrdered,
  Archive,
} from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { pb } from "@/lib/pb";
import { resolveAssetUrl, getToken, queryKeys } from "@/lib/api";
import { useDisplayQueue, useUpdateQueuePosition, useRemoveFromQueue, type QueueRecord } from "@/hooks/use-api";
import { parseRecordDate } from "@/lib/display-feed";

interface ProgrammeTabProps {
  slug: string;
}

// ─── Vignette photo + lightbox ────────────────────────────────────

function PhotoThumb({ src, alt }: { src: string; alt: string }) {
  const token = getToken();
  const url = resolveAssetUrl(src, token);

  return (
    <Dialog.Root>
      <Dialog.Trigger>
        <Box
          style={{
            width: 48,
            height: 48,
            borderRadius: "var(--radius-2)",
            overflow: "hidden",
            flexShrink: 0,
            cursor: "pointer",
            backgroundColor: "var(--gray-3)",
          }}
        >
          <img
            src={url}
            alt={alt}
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
            draggable={false}
          />
        </Box>
      </Dialog.Trigger>
      <Dialog.Content
        style={{ maxWidth: "90vw", maxHeight: "90vh", padding: 0, overflow: "hidden" }}
      >
        <img
          src={url}
          alt={alt}
          style={{ width: "100%", height: "auto", display: "block", maxHeight: "90vh", objectFit: "contain" }}
        />
      </Dialog.Content>
    </Dialog.Root>
  );
}

// ─── Icône et libellé selon le type ──────────────────────────────

function ContentTypeIcon({ type }: { type: QueueRecord["content_type"] }) {
  if (type === "photo") return <Image size={14} />;
  if (type === "weather_card") return <CloudSun size={14} />;
  return <MessageSquare size={14} />;
}

function contentTypeLabel(type: QueueRecord["content_type"]): string {
  if (type === "photo") return "Photo";
  if (type === "weather_card") return "Météo";
  return "Message";
}

function contentTypeBadgeColor(type: QueueRecord["content_type"]): "blue" | "orange" | "green" {
  if (type === "photo") return "blue";
  if (type === "weather_card") return "orange";
  return "green";
}

// ─── Résumé du contenu ────────────────────────────────────────────

function itemSummary(item: QueueRecord): string {
  const d = item.data;
  if (item.content_type === "message") {
    const text = typeof d.text === "string" ? d.text : "";
    return text.length > 60 ? text.slice(0, 57) + "…" : text;
  }
  if (item.content_type === "photo") {
    const caption = typeof d.caption === "string" ? d.caption : "";
    return caption || "Photo sans légende";
  }
  if (item.content_type === "weather_card") {
    const city = typeof d.city_label === "string" ? d.city_label : "";
    return city || "Carte météo";
  }
  return "";
}

// ─── Heure de création ────────────────────────────────────────────

function ItemTime({ created }: { created: string }) {
  const ms = parseRecordDate(created);
  if (!ms) return null;
  const d = new Date(ms);
  const h = d.getHours().toString().padStart(2, "0");
  const m = d.getMinutes().toString().padStart(2, "0");
  return (
    <Text size="1" color="gray" style={{ fontVariantNumeric: "tabular-nums", flexShrink: 0 }}>
      {h}:{m}
    </Text>
  );
}

// ─── Item de la file (draggable) ──────────────────────────────────

interface QueueItemRowProps {
  item: QueueRecord;
  index: number;
  isDragOver: boolean;
  onDragStart: (index: number) => void;
  onDragOver: (index: number) => void;
  onDrop: (index: number) => void;
  onDragEnd: () => void;
  onRemove: (id: string) => void;
  isRemoving: boolean;
}

function QueueItemRow({
  item,
  index,
  isDragOver,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
  onRemove,
  isRemoving,
}: QueueItemRowProps) {
  const imageUrl = item.content_type === "photo"
    ? (typeof item.data.image_url === "string" ? item.data.image_url : "")
    : "";
  const caption = typeof item.data.caption === "string" ? item.data.caption : "";

  return (
    <Card
      draggable
      onDragStart={() => onDragStart(index)}
      onDragOver={(e) => { e.preventDefault(); onDragOver(index); }}
      onDrop={(e) => { e.preventDefault(); onDrop(index); }}
      onDragEnd={onDragEnd}
      style={{
        cursor: "grab",
        opacity: isRemoving ? 0.5 : 1,
        borderTop: isDragOver ? "2px solid var(--accent-9)" : "2px solid transparent",
        transition: "border-color 0.1s, opacity 0.15s",
        userSelect: "none",
      }}
    >
      <Flex align="center" gap="3" py="1">

        {/* Poignée DnD */}
        <Box style={{ color: "var(--gray-8)", flexShrink: 0 }}>
          <GripVertical size={16} />
        </Box>

        {/* Position */}
        <Text size="1" color="gray" style={{ width: "1.5rem", textAlign: "right", flexShrink: 0 }}>
          {index + 1}
        </Text>

        {/* Vignette photo ou icône type */}
        {imageUrl ? (
          <PhotoThumb src={imageUrl} alt={caption || `Photo ${index + 1}`} />
        ) : (
          <Badge color={contentTypeBadgeColor(item.content_type)} size="1" style={{ flexShrink: 0 }}>
            <ContentTypeIcon type={item.content_type} />
            {contentTypeLabel(item.content_type)}
          </Badge>
        )}

        {/* Pour les photos : badge type EN PLUS de la vignette */}
        {imageUrl && (
          <Badge color="blue" size="1" variant="soft" style={{ flexShrink: 0 }}>
            <Image size={11} />
            Photo
          </Badge>
        )}

        {/* Contenu */}
        <Text
          size="2"
          style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
        >
          {itemSummary(item)}
        </Text>

        {/* Heure */}
        <ItemTime created={item.created} />

        {/* Supprimer de la file */}
        <Tooltip content="Retirer de la file (le contenu est conservé)">
          <IconButton
            size="1"
            variant="ghost"
            color="red"
            onClick={() => onRemove(item.id)}
            disabled={isRemoving}
          >
            <Trash2 size={13} />
          </IconButton>
        </Tooltip>
      </Flex>
    </Card>
  );
}

// ─── Composant principal ──────────────────────────────────────────

export function ProgrammeTab({ slug }: ProgrammeTabProps) {
  const { data, isLoading } = useDisplayQueue(slug);
  const updatePos = useUpdateQueuePosition(slug);
  const removeItem = useRemoveFromQueue(slug);
  const qc = useQueryClient();

  // ── Drag & drop state ─────────────────────────────────────────
  const dragIndexRef = useRef<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);

  // ── Realtime — invalidation sur display_queue ─────────────────
  useEffect(() => {
    const queueKey = [...queryKeys.display(slug), "queue"];
    let unsub: (() => void) | null = null;

    pb.collection("display_queue").subscribe("*", () => {
      qc.invalidateQueries({ queryKey: queueKey });
    }).then((fn) => { unsub = fn; }).catch(() => {});

    return () => { unsub?.(); };
  }, [slug, qc]);

  // ── Handlers DnD ──────────────────────────────────────────────

  function handleDragStart(index: number) {
    dragIndexRef.current = index;
  }

  function handleDragOver(index: number) {
    if (dragIndexRef.current === null || dragIndexRef.current === index) return;
    setDragOverIndex(index);
  }

  function handleDrop(toIndex: number) {
    const fromIndex = dragIndexRef.current;
    if (fromIndex === null || fromIndex === toIndex || !data) return;

    const items = data.queue;
    const item = items[fromIndex];
    if (!item) return;

    const filtered = items.filter((_, i) => i !== fromIndex);
    const insertAt = fromIndex < toIndex ? toIndex - 1 : toIndex;

    const before = filtered[insertAt - 1];
    const after = filtered[insertAt];

    let newPosition: number;
    if (!before && after) {
      newPosition = after.position - 1000;
      if (newPosition === 0) newPosition = -1000;
    } else if (before && !after) {
      newPosition = before.position + 1000;
    } else if (before && after) {
      newPosition = (before.position + after.position) / 2;
    } else {
      return;
    }

    updatePos.mutate({ itemId: item.id, position: newPosition });
    dragIndexRef.current = null;
    setDragOverIndex(null);
  }

  function handleDragEnd() {
    dragIndexRef.current = null;
    setDragOverIndex(null);
  }

  function handleRemove(itemId: string) {
    setRemovingId(itemId);
    removeItem.mutate(itemId, { onSettled: () => setRemovingId(null) });
  }

  // ── Rendu ──────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <Flex direction="column" gap="2">
        {[1, 2, 3].map((i) => <Skeleton key={i} height="64px" />)}
      </Flex>
    );
  }

  const queue = data?.queue ?? [];
  const archiveCount = data?.archive_count ?? 0;

  return (
    <Flex direction="column" gap="4">

      {/* ── En-tête file du jour ────────────────────────────── */}
      <Flex align="center" justify="between">
        <Flex align="center" gap="2">
          <ListOrdered size={16} />
          <Text weight="medium">File du jour</Text>
          <Badge color="gray" size="1">{queue.length} item{queue.length !== 1 ? "s" : ""}</Badge>
        </Flex>
        <Text size="1" color="gray">Glisser–déposer pour réordonner</Text>
      </Flex>

      {/* ── Liste drag & drop ───────────────────────────────── */}
      {queue.length === 0 ? (
        <Card>
          <Flex align="center" justify="center" py="6" direction="column" gap="2">
            <ListOrdered size={24} color="var(--gray-7)" />
            <Text size="2" color="gray" align="center">
              La file est vide pour aujourd'hui.
              <br />
              Envoyez un message ou uploadez une photo pour commencer.
            </Text>
          </Flex>
        </Card>
      ) : (
        <Flex direction="column" gap="1">
          {queue.map((item, index) => (
            <QueueItemRow
              key={item.id}
              item={item}
              index={index}
              isDragOver={dragOverIndex === index}
              onDragStart={handleDragStart}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onDragEnd={handleDragEnd}
              onRemove={handleRemove}
              isRemoving={removingId === item.id}
            />
          ))}
        </Flex>
      )}

      {/* ── Pool d'archives ─────────────────────────────────── */}
      <Card variant="surface">
        <Flex align="center" gap="3">
          <Archive size={16} color="var(--gray-9)" />
          <Box style={{ flex: 1 }}>
            <Text size="2" weight="medium">Pool d'archives</Text>
            <Text size="1" color="gray" as="p">
              {archiveCount === 0
                ? "Aucune photo archivée pour l'instant."
                : `${archiveCount} photo${archiveCount > 1 ? "s" : ""} dans le catalogue — diffusées aléatoirement par la tablette en rotation.`}
            </Text>
          </Box>
          <Badge color="gray" variant="soft" size="2">{archiveCount}</Badge>
        </Flex>
      </Card>
    </Flex>
  );
}
